// dev-portal/src/pages/auth/InviteSignup.jsx

import { useState, useEffect } from 'react';
import { Eye, EyeOff, Shield, User as UserIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import Footer from '../../components/shared/Footer';
import PasswordValidation, { validatePassword } from '../../components/shared/PasswordValidation';

export default function InviteSignup({ token }) {
  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [checking, setChecking] = useState(true);

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [website, setWebsite] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/api/company/invite/${token}`)
      .then((res) => setInvite(res.data))
      .catch((err) => setLoadError(err.response?.data?.detail || 'This invite link is invalid or has expired'))
      .finally(() => setChecking(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!companyName.trim()) {
      setError('Company name is required');
      return;
    }

    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      setError(passwordErrors[0]);
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await api.post('/api/company/accept-invite', {
        token,
        password,
        company_name: companyName,
        website,
      });
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('role', 'developer_software_company');
      window.location.href = '/dashboard';
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to complete signup');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 py-8">
        <div className="bg-white shadow-lg rounded-lg px-8 pt-6 pb-8 w-full max-w-md">
          <h2 className="text-2xl font-bold mb-2 text-center text-gray-800">
            Complete Your Signup
          </h2>

          {checking && (
            <p className="text-center text-gray-500 text-sm py-8">Checking your invite...</p>
          )}

          {!checking && loadError && (
            <>
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
                {loadError}
              </div>
              <div className="text-center text-sm text-gray-600">
                <Link to="/login" className="text-blue-600 hover:underline font-medium">
                  Go to Login
                </Link>
              </div>
            </>
          )}

          {!checking && invite && (
            <>
              <p className="text-center text-gray-500 text-sm mb-6">
                You've been invited to join with{' '}
                <span className="inline-flex items-center gap-1 font-medium text-gray-700">
                  {invite.role === 'owner' ? <Shield className="w-3.5 h-3.5" /> : <UserIcon className="w-3.5 h-3.5" />}
                  {invite.role === 'owner' ? 'Full Access' : 'Limited Access'}
                </span>
              </p>

              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Name</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                    type="text"
                    value={`${invite.first_name} ${invite.last_name}`}
                    disabled
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Email</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                    type="email"
                    value={invite.email}
                    disabled
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Website</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                    type="text"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Password <span className="text-red-500">*</span>
                    <span className="text-gray-500 font-normal"> (8-12 characters)</span>
                  </label>
                  <div className="relative">
                    <input
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10 text-gray-900 bg-white"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      maxLength={12}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                    </button>
                  </div>
                  <PasswordValidation password={password} />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-green-600 text-white py-3 rounded-md hover:bg-green-700 transition-colors font-semibold mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Completing Signup...' : 'Complete Signup'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}
